/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 * Cup Mockup Service - Client-side Canvas Rendering
 */

/**
 * Helper: Vẽ chữ thành ảnh (Logo Text) và Auto-Crop khoảng trắng thừa
 */
export const renderTextToImage = (text: string, fontFamily: string = "serif"): string => {
    // 1. Tạo Canvas tạm thời kích thước lớn
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) return "";

    canvas.width = 2000;
    canvas.height = 2000;

    ctx.clearRect(0, 0, canvas.width, canvas.height);

    ctx.fillStyle = "black";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";

    let fontName = "serif";
    if (fontFamily === "Inter") fontName = "sans-serif";
    if (fontFamily === "Playfair Display") fontName = "serif";
    if (fontFamily === "Bebas Neue") fontName = "sans-serif";
    if (fontFamily === "Dancing Script") fontName = "cursive";
    if (fontFamily === "Courier Prime") fontName = "monospace";

    let fontSize = 500; // Tăng size gốc lên
    const maxWidth = 1900;
    ctx.font = `bold ${fontSize}px "${fontName}"`;
    let metrics = ctx.measureText(text);

    // Điều chỉnh font size cho vừa khung
    while (metrics.width > maxWidth && fontSize > 50) {
        fontSize -= 10;
        ctx.font = `bold ${fontSize}px "${fontName}"`;
        metrics = ctx.measureText(text);
    }

    // Vẽ chữ vào giữa canvas lớn
    ctx.fillText(text, canvas.width / 2, canvas.height / 2);

    // 2. THUẬT TOÁN AUTO-CROP (Cắt bỏ viền trong suốt)
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const data = imageData.data;

    let minX = canvas.width, minY = canvas.height, maxX = 0, maxY = 0;
    let found = false;

    // Quét tìm vùng có pixel (Alpha > 0)
    for (let y = 0; y < canvas.height; y++) {
        for (let x = 0; x < canvas.width; x++) {
            const alpha = data[(y * canvas.width + x) * 4 + 3];
            if (alpha > 20) {
                if (x < minX) minX = x;
                if (x > maxX) maxX = x;
                if (y < minY) minY = y;
                if (y > maxY) maxY = y;
                found = true;
            }
        }
    }

    // Nếu không vẽ được gì (toàn bộ trong suốt)
    if (!found) return canvas.toDataURL("image/png");

    // 3. Tạo Canvas kết quả vừa khít với chữ
    const padding = 0; // Cắt sát viền để tối đa kích thước
    const croppedWidth = (maxX - minX) + (padding * 2);
    const croppedHeight = (maxY - minY) + (padding * 2);

    const finalCanvas = document.createElement('canvas');
    finalCanvas.width = croppedWidth;
    finalCanvas.height = croppedHeight;
    const fCtx = finalCanvas.getContext('2d');

    if (fCtx) {
        // Vẽ phần đã crop sang canvas mới
        fCtx.drawImage(
            canvas,
            minX, minY, maxX - minX, maxY - minY, // Source Rect (Vùng chữ)
            padding, padding, maxX - minX, maxY - minY // Dest Rect (Vẽ vào giữa canvas mới)
        );
        return finalCanvas.toDataURL("image/png");
    }

    return canvas.toDataURL("image/png");
};

/**
 * Helper: Load ảnh
 */
const loadImage = (source: string | File): Promise<HTMLImageElement> => {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = "Anonymous";
        img.onload = () => resolve(img);
        img.onerror = () => reject(new Error("Failed to load image"));

        if (source instanceof File) {
            const reader = new FileReader();
            reader.onload = (e) => { img.src = e.target?.result as string; };
            reader.readAsDataURL(source);
        } else {
            img.src = source;
        }
    });
};

/**
 * XỬ LÝ MÀU LOGO NÂNG CAO (Luminance to Alpha Mapping):
 * Giữ lại chi tiết logo bằng cách map độ đậm nhạt sang độ trong suốt của màu target.
 */
const processLogoColor = (img: HTMLImageElement, width: number, height: number): HTMLCanvasElement => {
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) return canvas;

    ctx.drawImage(img, 0, 0, width, height);
    const imageData = ctx.getImageData(0, 0, width, height);
    const data = imageData.data;

    // Target Color: #DEB887 -> RGB(222, 184, 135)
    const tr = 222, tg = 184, tb = 135;

    // Ngưỡng trắng để xóa nền
    const whiteThreshold = 230;

    for (let i = 0; i < data.length; i += 4) {
        const r = data[i];
        const g = data[i + 1];
        const b = data[i + 2];
        const a = data[i + 3];

        if (a < 10) continue; // Bỏ qua pixel trong suốt

        // 1. Tính độ sáng (Luminance) của pixel gốc: 0 (Đen) -> 255 (Trắng)
        const luminance = 0.299 * r + 0.587 * g + 0.114 * b;

        // 2. Xóa nền trắng
        // Nếu pixel rất sáng và không trong suốt -> Coi là nền -> Xóa
        if (luminance > whiteThreshold && a > 200) {
            data[i + 3] = 0;
            continue;
        }

        // 3. Map độ đậm nhạt (Detail Preservation)
        // Pixel càng tối (luminance thấp) -> Càng hiện rõ màu target (Opacity cao)
        // Pixel màu xám nhạt (luminance cao) -> Màu target mờ đi (Opacity thấp)

        // Đảo ngược độ sáng: Đen (0) -> Intensity 1.0, Trắng (255) -> Intensity 0.0
        let intensity = 1 - (luminance / 255);

        // Tăng cường độ tương phản (Contrast Curve) để logo rõ hơn
        // Công thức mũ giúp các vùng xám trở nên đậm hơn một chút
        intensity = Math.pow(intensity, 0.7);

        // Gán màu đích
        data[i] = tr;
        data[i + 1] = tg;
        data[i + 2] = tb;

        // Gán Alpha dựa trên độ đậm gốc * Alpha gốc
        // Đảm bảo không vượt quá 255
        data[i + 3] = Math.floor(intensity * 255 * (a / 255));
    }

    ctx.putImageData(imageData, 0, 0);
    return canvas;
}

interface BoundingBox {
    x: number;
    y: number;
    width: number;
    height: number;
}

/**
 * THUẬT TOÁN: Flood Fill để tách các Box đỏ rời nhau
 */
const detectRedBoxes = (ctx: CanvasRenderingContext2D, width: number, height: number): BoundingBox[] => {
    const imageData = ctx.getImageData(0, 0, width, height);
    const data = imageData.data;
    const visited = new Uint8Array(width * height);
    const boxes: BoundingBox[] = [];

    const isRed = (idx: number) => {
        const r = data[idx];
        const g = data[idx + 1];
        const b = data[idx + 2];
        // Ngưỡng đỏ
        return r > 120 && g < 100 && b < 100;
    };

    for (let y = 0; y < height; y++) {
        for (let x = 0; x < width; x++) {
            const index = (y * width + x);
            const pixelIndex = index * 4;

            if (visited[index]) continue;

            if (isRed(pixelIndex)) {
                let minX = x, maxX = x, minY = y, maxY = y;
                const stack = [index];
                visited[index] = 1;
                let count = 0;

                while (stack.length > 0) {
                    const currIdx = stack.pop()!;
                    const currX = currIdx % width;
                    const currY = Math.floor(currIdx / width);

                    count++;
                    if (currX < minX) minX = currX;
                    if (currX > maxX) maxX = currX;
                    if (currY < minY) minY = currY;
                    if (currY > maxY) maxY = currY;

                    const neighbors = [currIdx - width, currIdx + width, currIdx - 1, currIdx + 1];

                    for (const n of neighbors) {
                        if (n < 0 || n >= width * height) continue;
                        if (Math.abs((n % width) - currX) > 1) continue;

                        if (!visited[n] && isRed(n * 4)) {
                            visited[n] = 1;
                            stack.push(n);
                        }
                    }
                }

                if (count > 200 && (maxX - minX) > 10 && (maxY - minY) > 10) {
                    boxes.push({
                        x: minX,
                        y: minY,
                        width: maxX - minX,
                        height: maxY - minY
                    });
                }
            }
        }
    }


    return boxes;
};

/**
 * Xử lý xóa vạch đỏ (Chỉ dùng khi KHÔNG có ảnh Mask riêng biệt)
 */
const removeRedGuides = (ctx: CanvasRenderingContext2D, width: number, height: number) => {
    const imageData = ctx.getImageData(0, 0, width, height);
    const data = imageData.data;

    for (let i = 0; i < data.length; i += 4) {
        const r = data[i];
        const g = data[i + 1];
        const b = data[i + 2];

        // Nếu là màu đỏ guide
        if (r > 120 && g < 100 && b < 100) {
            // Chuyển sang thang độ xám (Desaturate)
            const avg = (r + g + b) / 3;
            data[i] = avg;
            data[i + 1] = avg;
            data[i + 2] = avg;
        }
    }
    ctx.putImageData(imageData, 0, 0);
};

/**
 * CORE LOGIC: Ghép Logo (DUAL IMAGE MODE)
 * @param productSource Ảnh sạch (Clean Image) để hiển thị
 * @param maskSource Ảnh chứa khung đỏ (Mask Image) để dò tọa độ
 * @param designSource Logo
 */
const compositeDesignOnProduct = async (
    productSource: string | File,
    maskSource: string | File | undefined,
    designSource: string | File,
    textOverlay: string,
    fontFamily: string
): Promise<{ composite: string }> => {

    // 1. Load ảnh Clean (Product)
    const productImg = await loadImage(productSource);

    // Setup Main Canvas
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error("No context");

    const maxSize = 1024;
    let w = productImg.width;
    let h = productImg.height;
    const scale = Math.min(maxSize / w, maxSize / h);

    canvas.width = w * scale;
    canvas.height = h * scale;

    // Vẽ ảnh Clean lên Main Canvas
    ctx.drawImage(productImg, 0, 0, canvas.width, canvas.height);

    // 2. Xử lý Mask & Detection
    let boxes: BoundingBox[] = [];

    if (maskSource) {
        // TRƯỜNG HỢP CÓ ẢNH MASK: Dò trên ảnh Mask
        const maskImg = await loadImage(maskSource);

        // Tạo Canvas phụ để vẽ Mask
        const maskCanvas = document.createElement('canvas');
        maskCanvas.width = canvas.width;   // Kích thước phải khớp với Main Canvas
        maskCanvas.height = canvas.height;
        const maskCtx = maskCanvas.getContext('2d');

        if (maskCtx) {
            maskCtx.drawImage(maskImg, 0, 0, maskCanvas.width, maskCanvas.height);
            // Dò tìm box trên Canvas Mask
            boxes = detectRedBoxes(maskCtx, maskCanvas.width, maskCanvas.height);
        }
        // Lưu ý: Không cần removeRedGuides vì Main Canvas là ảnh Clean
    } else {
        // TRƯỜNG HỢP KHÔNG CÓ MASK (User upload 1 ảnh): Dò trực tiếp trên Main Canvas
        boxes = detectRedBoxes(ctx, canvas.width, canvas.height);

        // Nếu tìm thấy box đỏ trên ảnh chính, phải xóa đi cho đẹp
        if (boxes.length > 0) {
            removeRedGuides(ctx, canvas.width, canvas.height);
        }
    }

    // 3. Load và xử lý Logo (Dùng Luminance Mapping)
    const rawLogoImg = await loadImage(designSource);
    const processedLogoCanvas = processLogoColor(rawLogoImg, rawLogoImg.width, rawLogoImg.height);

    // 4. Hàm vẽ logo vào tọa độ đã tìm được
    const drawLogoAt = (box: BoundingBox | null) => {
        let targetX, targetY, targetW, targetH;

        if (box) {
            // Maximize size within the box (no padding)
            targetW = box.width;
            const ratio = processedLogoCanvas.height / processedLogoCanvas.width;
            targetH = targetW * ratio;

            if (targetH > box.height) {
                targetH = box.height;
                targetW = targetH / ratio;
            }

            targetX = box.x + (box.width - targetW) / 2;
            targetY = box.y + (box.height - targetH) / 2;
        } else {
            // Fallback: Giữa ảnh nếu không tìm thấy box
            targetW = canvas.width * 0.4;
            const ratio = processedLogoCanvas.height / processedLogoCanvas.width;
            targetH = targetW * ratio;
            targetX = (canvas.width - targetW) / 2;
            targetY = (canvas.height - targetH) / 2;
        }

        ctx.drawImage(processedLogoCanvas, targetX, targetY, targetW, targetH);

        if (textOverlay) {
            const textPadding = targetH * 0.15;
            ctx.fillStyle = "#CDB580";
            ctx.textAlign = "center";
            ctx.textBaseline = "top";
            const fontSize = Math.max(14, Math.floor(targetW * 0.12));

            let fontName = "serif";
            if (fontFamily === "Inter") fontName = "sans-serif";
            if (fontFamily === "Playfair Display") fontName = "serif";

            ctx.font = `bold ${fontSize}px "${fontName}"`;
            ctx.fillText(textOverlay, targetX + targetW / 2, targetY + targetH + textPadding);
        }
    };

    if (boxes.length > 0) {
        boxes.forEach(box => drawLogoAt(box));
    } else {
        drawLogoAt(null);
    }

    return {
        composite: canvas.toDataURL("image/png").split(',')[1]
    };
};

/**
 * Generate text logo
 */
export const generateTextLogo = async (text: string): Promise<string> => {
    return renderTextToImage(text);
};

/**
 * Generate cup mockup with client-side canvas rendering
 */
export const generateCupMockup = async (
    designImageUrl: string,
    productImage: File | string, // Ảnh sạch
    maskImage?: File | string,   // Ảnh chứa khung đỏ (Optional)
    options?: {
        removeBackground?: boolean;
        customPrompt?: string;
        textOverlay?: string;
        fontFamily?: string;
        printStyle?: string;
    }
): Promise<{ resultImage: string }> => {

    const { composite } = await compositeDesignOnProduct(
        productImage,
        maskImage,
        designImageUrl,
        options?.textOverlay || "",
        options?.fontFamily || "Inter"
    );

    const resultImage = `data:image/png;base64,${composite}`;

    return { resultImage };
};
